import React, { useCallback, useEffect, useState } from "react";
import { FaPhoneAlt, FaSlidersH, FaInfoCircle } from "react-icons/fa";
import { SectionCard } from "../SectionCard";
import { useInView } from "../useInView";
import { fetchOrgVoiceSettings, fetchOrgRecentVoiceCalls } from "../api";
import { formatRelative } from "../format";
import {
  backgroundSoundLabel,
  describeRecentCall,
  isRouteMissing,
  modelLabel,
  voiceByKey,
  voiceLabel,
  whoSpeaksFirstLabel,
} from "../voiceSettings";
import VoiceSettingsDialog from "./voice/VoiceSettingsDialog";

const ACCENT = "#0ea5e9";

/**
 * Org 360 voice assistant section: what the assistant uses when it phones this
 * organization's borrowers, the newest calls with their models and cost, and
 * the configuration dialog.
 */
export default function VoiceSettingsSection({ organizationId, orgName, env }) {
  const [ref, inView] = useInView();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notDeployed, setNotDeployed] = useState(false);
  const [data, setData] = useState(null);
  const [calls, setCalls] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotDeployed(false);
    try {
      const [settingsJson, callsJson] = await Promise.all([
        fetchOrgVoiceSettings(organizationId, env),
        fetchOrgRecentVoiceCalls(organizationId, env).catch(() => ({ calls: [] })),
      ]);
      setData(settingsJson);
      setCalls(Array.isArray(callsJson?.calls) ? callsJson.calls : []);
    } catch (err) {
      if (isRouteMissing(err)) {
        setNotDeployed(true);
      } else {
        setError(err?.message || "Failed to load voice settings");
      }
    } finally {
      setLoading(false);
    }
  }, [organizationId, env]);

  useEffect(() => {
    if (inView) load();
  }, [inView, load]);

  const settings = data?.settings;
  const voices = data?.options?.voices || [];
  const voice = settings ? voiceByKey(voices, settings.voice?.key) : null;
  const presetLabel = settings?.wording?.personalityPreset
    ? (data?.options?.personalityPresets || []).find(
        (preset) => preset.id === settings.wording.personalityPreset,
      )?.label || settings.wording.personalityPreset
    : null;
  const updatedChip = settings?.updatedAt ? (
    <span className="oo-chip">Saved {formatRelative(settings.updatedAt)}</span>
  ) : (
    <span className="oo-chip">Defaults</span>
  );

  return (
    <SectionCard
      ref={ref}
      title="Voice Assistant"
      subtitle="How Addy phones this organization's borrowers"
      icon={<FaPhoneAlt />}
      accent={ACCENT}
      loading={(loading || !inView) && !data && !notDeployed}
      error={error}
      onRetry={load}
      size="full"
      headerRight={settings ? updatedChip : null}
      link={{
        to: { pathname: "/voice-console", state: { organizationId } },
        label: "Open in the voice agent editor",
      }}
    >
      {notDeployed ? (
        <div className="oo-card-state">
          <FaInfoCircle />
          <div className="oo-card-state-text">
            Voice settings are not deployed on <strong>{env}</strong> yet. The voice routes run on
            the dev backend; switch the environment to dev, or wait for the next production
            release.
          </div>
        </div>
      ) : null}

      {settings ? (
        <>
          <div className="oo-retention-meta">
            <span className="oo-chip" title={settings.voice?.ttsSpec}>
              Voice: {voiceLabel(voice)}
              {settings.voice?.speed && settings.voice.speed !== 1
                ? ` · ${Number(settings.voice.speed).toFixed(2)}×`
                : ""}
            </span>
            <span className="oo-chip" title={settings.models.llm}>
              Brain: {modelLabel(settings.models.llm)}
            </span>
            <span className="oo-chip" title={settings.models.stt}>
              Ear: {modelLabel(settings.models.stt)}
            </span>
            <span className="oo-chip">{whoSpeaksFirstLabel(settings.call?.whoSpeaksFirst)}</span>
            {settings.sound?.background && settings.sound.background !== "off" ? (
              <span className="oo-chip">
                Sound: {backgroundSoundLabel(settings.sound.background)}
              </span>
            ) : null}
            <span className={`oo-chip ${settings.voicemail.detect ? "oo-chip--ok" : ""}`}>
              Voicemail: {settings.voicemail.detect ? "detect" : "off"}
              {settings.voicemail.leaveMessage ? ", leave a message" : ""}
            </span>
            <span className="oo-chip">Cap: {Math.round(settings.call.maxDurationS / 60)} min</span>
            <span className="oo-chip">
              Zone: {settings.callingHours.timezone || "from the property state"}
            </span>
            {presetLabel ? <span className="oo-chip oo-chip--ok">Tone: {presetLabel}</span> : null}
            {settings.wording?.greeting ? (
              <span className="oo-chip oo-chip--ok">Custom greeting</span>
            ) : null}
            {settings.wording?.promptAdditions ? (
              <span className="oo-chip oo-chip--ok">Prompt additions set</span>
            ) : null}
            {settings.analysis?.sentiment ? <span className="oo-chip">Sentiment on</span> : null}
          </div>

          {calls.length ? (
            <div className="oo-send-history">
              <div className="oo-send-history-head">Recent calls</div>
              <table className="oo-send-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Outcome</th>
                    <th>Length</th>
                    <th>Cost</th>
                    <th>Voice</th>
                    <th>Sentiment</th>
                    <th>Documents</th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((call) => {
                    const row = describeRecentCall(call, voices);
                    return (
                      <tr key={row.id}>
                        <td>{row.when ? formatRelative(new Date(row.when).getTime()) : "—"}</td>
                        <td>
                          <span className={`oo-chip oo-chip--${row.tone}`}>{row.outcome}</span>
                        </td>
                        <td>{row.duration}</td>
                        <td>{row.cost}</td>
                        <td>{row.voice}</td>
                        <td>{row.sentiment || "—"}</td>
                        <td>{row.marked ? "marked requested" : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="oo-empty-hint" style={{ textAlign: "left" }}>
              No calls have been placed for this organization yet.
            </div>
          )}

          <button
            type="button"
            className="oo-mini-btn oo-mini-btn--primary"
            style={{ marginTop: 12 }}
            onClick={() => setDialogOpen(true)}
          >
            <FaSlidersH style={{ fontSize: 11 }} /> Configure voice assistant
          </button>

          {dialogOpen ? (
            <VoiceSettingsDialog
              organizationId={organizationId}
              env={env}
              data={data}
              orgName={orgName || organizationId}
              onClose={() => setDialogOpen(false)}
              onSaved={(saved) => {
                if (saved) setData((prev) => ({ ...prev, settings: saved }));
              }}
            />
          ) : null}
        </>
      ) : null}
    </SectionCard>
  );
}
